import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './Homework4Question.css';

const Homework4Question1: React.FC = () => {
  const [originalImage, setOriginalImage] = useState<string | null>(null);
  const [minFiltered, setMinFiltered] = useState<string | null>(null);
  const [maxFiltered, setMaxFiltered] = useState<string | null>(null);
  const [avgFiltered, setAvgFiltered] = useState<string | null>(null);
  const [medianFiltered, setMedianFiltered] = useState<string | null>(null);
  const [windowSize, setWindowSize] = useState<number>(3);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-load sample image on component mount
  useEffect(() => {
    const loadSampleImage = async () => {
      try {
        const response = await fetch('/sample-images/HW4_1.png');
        const blob = await response.blob();
        const reader = new FileReader();
        reader.onload = (e) => {
          setOriginalImage(e.target?.result as string);
        };
        reader.readAsDataURL(blob);
      } catch (err) {
        console.error('Failed to load sample image:', err);
      }
    };
    loadSampleImage();
  }, []);

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setOriginalImage(e.target?.result as string);
        // Clear previous results
        setMinFiltered(null);
        setMaxFiltered(null);
        setAvgFiltered(null);
        setMedianFiltered(null);
        setError(null);
      };
      reader.readAsDataURL(file);
    }
  };

  const applyAllFilters = async () => {
    if (!originalImage) {
      setError('Please upload an image first');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const base64Image = originalImage.split(',')[1];

      // Apply all four filters in parallel
      const [minRes, maxRes, avgRes, medianRes] = await Promise.all([
        axios.post(
          'https://process-homework4-q1-72eav6542q-uc.a.run.app',
          {
            image: base64Image,
            filter_type: 'min',
            window_size: windowSize
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          }
        ),
        axios.post(
          'https://process-homework4-q1-72eav6542q-uc.a.run.app',
          {
            image: base64Image,
            filter_type: 'max',
            window_size: windowSize
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          }
        ),
        axios.post(
          'https://process-homework4-q1-72eav6542q-uc.a.run.app',
          {
            image: base64Image,
            filter_type: 'average',
            window_size: windowSize
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          }
        ),
        axios.post(
          'https://process-homework4-q1-72eav6542q-uc.a.run.app',
          {
            image: base64Image,
            filter_type: 'median',
            window_size: windowSize
          },
          {
            headers: { 'Content-Type': 'application/json' },
            timeout: 30000
          }
        )
      ]);

      // Set results
      if (minRes.data.success) {
        setMinFiltered(`data:image/png;base64,${minRes.data.processed_image}`);
      }
      if (maxRes.data.success) {
        setMaxFiltered(`data:image/png;base64,${maxRes.data.processed_image}`);
      }
      if (avgRes.data.success) {
        setAvgFiltered(`data:image/png;base64,${avgRes.data.processed_image}`);
      }
      if (medianRes.data.success) {
        setMedianFiltered(`data:image/png;base64,${medianRes.data.processed_image}`);
      }

    } catch (err: any) {
      console.error('Error:', err);
      setError(err.response?.data?.error || err.message || 'Failed to process image');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="hw4-question-container">
      <div className="floating-shapes">
        <div className="shape shape-1"></div>
        <div className="shape shape-2"></div>
      </div>

      <div className="content-wrapper">
        <Link to="/homework/4" className="back-button">
          ← Back to Homework 4
        </Link>

        <header className="question-header">
          <div className="badge">Question 1</div>
          <h1>Salt-and-Pepper Noise Reduction</h1>
          <p className="subtitle">Spatial filtering using min, max, average, and median filters</p>
        </header>

        <div className="algorithm-info">
          <h3>Spatial Filters</h3>
          <p>
            Salt-and-pepper noise appears as random white (salt) and black (pepper) pixels.
            Four different spatial filters are compared:
          </p>
          <ul>
            <li><strong>Min Filter:</strong> Takes minimum value in window - removes white noise (salt)</li>
            <li><strong>Max Filter:</strong> Takes maximum value in window - removes black noise (pepper)</li>
            <li><strong>Average Filter:</strong> Computes mean value - smooths both noise types but may blur</li>
            <li><strong>Median Filter:</strong> Takes median value - best for salt-and-pepper, preserves edges</li>
          </ul>
        </div>

        <div className="controls-section">
          <div className="control-group">
            <label htmlFor="window-size">Window Size:</label>
            <select
              id="window-size"
              value={windowSize}
              onChange={(e) => setWindowSize(Number(e.target.value))}
              className="window-select"
            >
              <option value={3}>3×3</option>
              <option value={5}>5×5</option>
              <option value={7}>7×7</option>
            </select>
          </div>

          <div className="upload-area">
            <label htmlFor="image-upload" className="upload-label">
              <span className="upload-icon">📁</span>
              <span>Choose Image</span>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                style={{ display: 'none' }}
              />
            </label>
            <button
              onClick={applyAllFilters}
              disabled={!originalImage || loading}
              className="process-button"
            >
              {loading ? 'Processing...' : 'Apply All Filters'}
            </button>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </div>

        <div className="images-grid-5">
          <div className="image-panel">
            <h3>Original (Noisy)</h3>
            <div className="image-container">
              {originalImage ? (
                <img src={originalImage} alt="Original" />
              ) : (
                <div className="placeholder">Upload an image</div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Min Filter</h3>
            <div className="image-container">
              {minFiltered ? (
                <img src={minFiltered} alt="Min Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Max Filter</h3>
            <div className="image-container">
              {maxFiltered ? (
                <img src={maxFiltered} alt="Max Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Average Filter</h3>
            <div className="image-container">
              {avgFiltered ? (
                <img src={avgFiltered} alt="Average Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>

          <div className="image-panel">
            <h3>Median Filter</h3>
            <div className="image-container">
              {medianFiltered ? (
                <img src={medianFiltered} alt="Median Filtered" />
              ) : (
                <div className="placeholder">
                  {loading ? 'Processing...' : 'Result will appear here'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="info-box">
          <h4>Analysis Tips</h4>
          <p>
            Compare the four results to see which filter works best for your image.
            The median filter typically provides the best balance between noise reduction
            and edge preservation for salt-and-pepper noise.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Homework4Question1;
